import React, { useCallback, useEffect, useState } from 'react';
import {
  trainingService,
  AcademyMediaAsset,
  AdminAcademyContentOverview,
  AdminAcademyModuleDetail,
  AdminAcademyModulePayload,
  AdminAcademyModuleSummary,
  LessonMediaItem,
  LessonMediaType,
} from '../../services/trainingService';
import AcademyModulePreview from './AcademyModulePreview';
import LessonMediaPicker from './LessonMediaPicker';

interface LessonDraft {
  id?: string | null;
  title: string;
  duration: string;
  summary: string;
  contentMarkdown: string;
  takeawaysText: string;
  media: LessonMediaItem[];
}

interface QuestionDraft {
  id?: string | null;
  question: string;
  optionsText: string;
  correctIndex: number;
  explanation: string;
}

interface ModuleDraft {
  slug: string | null;
  title: string;
  description: string;
  duration: string;
  durationMinutes: number;
  category: string;
  isoClause: string;
  sortOrder: number;
  status: string;
  version: number;
  publishedVersion: number | null;
  draftVersion: number | null;
  lessons: LessonDraft[];
  quiz: QuestionDraft[];
}

const blankLesson = (): LessonDraft => ({
  id: null,
  title: '',
  duration: '',
  summary: '',
  contentMarkdown: '',
  takeawaysText: '',
  media: [],
});

const blankQuestion = (): QuestionDraft => ({
  id: null,
  question: '',
  optionsText: '',
  correctIndex: 0,
  explanation: '',
});

const blankModule = (): ModuleDraft => ({
  slug: null,
  title: '',
  description: '',
  duration: '',
  durationMinutes: 60,
  category: '',
  isoClause: '',
  sortOrder: 0,
  status: 'draft',
  version: 0,
  publishedVersion: null,
  draftVersion: null,
  lessons: [blankLesson()],
  quiz: [blankQuestion()],
});

const toDraft = (detail: AdminAcademyModuleDetail): ModuleDraft => ({
  slug: detail.slug,
  title: detail.title,
  description: detail.description,
  duration: detail.duration,
  durationMinutes: detail.duration_minutes,
  category: detail.category,
  isoClause: detail.iso_clause,
  sortOrder: detail.sort_order,
  status: detail.status,
  version: detail.version,
  publishedVersion: detail.published_version,
  draftVersion: detail.draft_version,
  lessons: detail.lessons.map((lesson) => ({
    id: lesson.id,
    title: lesson.title,
    duration: lesson.duration,
    summary: lesson.summary,
    contentMarkdown: lesson.content_markdown,
    takeawaysText: lesson.takeaways.join('\n'),
    media: lesson.media || [],
  })),
  quiz: detail.quiz.map((question) => ({
    id: question.id,
    question: question.question,
    optionsText: question.options.join('\n'),
    correctIndex: question.correct_index,
    explanation: question.explanation,
  })),
});

const toPayload = (draft: ModuleDraft): AdminAcademyModulePayload => ({
  title: draft.title.trim(),
  description: draft.description,
  duration: draft.duration.trim(),
  duration_minutes: Number(draft.durationMinutes) || 0,
  category: draft.category.trim(),
  iso_clause: draft.isoClause.trim(),
  sort_order: Number(draft.sortOrder) || 0,
  lessons: draft.lessons.map((lesson) => ({
    id: lesson.id || undefined,
    title: lesson.title.trim(),
    duration: lesson.duration.trim(),
    summary: lesson.summary,
    content_markdown: lesson.contentMarkdown,
    takeaways: lesson.takeawaysText
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean),
    media: lesson.media.map((item) => ({
      asset_id: item.asset_id,
      type: item.type,
      caption: item.caption || '',
      alt_text: item.alt_text || '',
    })),
  })),
  quiz: draft.quiz.map((question) => ({
    id: question.id || undefined,
    question: question.question.trim(),
    options: question.optionsText
      .split('\n')
      .map((option) => option.trim())
      .filter(Boolean),
    correct_index: Number(question.correctIndex) || 0,
    explanation: question.explanation,
  })),
});

const statusStyle = (status: string) => {
  if (status === 'published') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status === 'draft') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const mediaTypeFor = (contentType: string): LessonMediaType => {
  if (contentType.startsWith('video/')) return 'video';
  if (contentType.startsWith('image/')) return 'image';
  return 'file';
};

const apiErrorMessage = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) =>
        typeof item === 'string' ? item : item?.msg ? String(item.msg) : ''
      )
      .filter(Boolean);
    if (messages.length) return messages.join(' ');
  }
  return fallback;
};

const formatBytes = (bytes: number) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const moveItem = <T,>(items: T[], from: number, to: number): T[] => {
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const StatChip: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
  </div>
);

const AcademyContentManager: React.FC = () => {
  const [overview, setOverview] = useState<AdminAcademyContentOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState<ModuleDraft | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await trainingService.getContentOverview();
      setOverview(data);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to load Academy content'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const openEditor = async (slug?: string) => {
    setError(null);
    setNotice(null);
    setPreviewOpen(false);
    if (!slug) {
      setDraft(blankModule());
      setEditorOpen(true);
      return;
    }
    try {
      setBusy(`load-${slug}`);
      const detail = await trainingService.getAdminModule(slug);
      setDraft(toDraft(detail));
      setEditorOpen(true);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to open that module'));
    } finally {
      setBusy(null);
    }
  };

  const handleImport = async () => {
    try {
      setBusy('import');
      setError(null);
      const data = await trainingService.importCurriculum();
      setOverview(data);
      setNotice('Bundled curriculum imported. Every module now has an editable published version.');
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Import failed'));
    } finally {
      setBusy(null);
    }
  };

  const persistDraft = async (): Promise<AdminAcademyModuleDetail | null> => {
    if (!draft) return null;
    if (draft.title.trim().length < 2) {
      setError('Give the module a title of at least two characters before saving.');
      return null;
    }
    if (draft.durationMinutes < 0 || draft.durationMinutes > 6000) {
      setError('Duration must be between 0 and 6000 minutes.');
      return null;
    }
    for (const lesson of draft.lessons) {
      if (!lesson.title.trim()) {
        setError('Every lesson needs a title before saving.');
        return null;
      }
    }
    for (const question of draft.quiz) {
      const options = question.optionsText
        .split('\n')
        .map((option) => option.trim())
        .filter(Boolean);
      if (!question.question.trim() || options.length < 2) {
        setError('Every quiz question needs text and at least two options.');
        return null;
      }
      if (question.correctIndex < 0 || question.correctIndex >= options.length) {
        setError('A quiz question points at an answer that no longer exists.');
        return null;
      }
    }

    const payload = toPayload(draft);
    try {
      setBusy('save');
      setError(null);
      const saved = draft.slug
        ? await trainingService.updateAdminModule(draft.slug, payload)
        : await trainingService.createAdminModule(payload);
      setDraft(toDraft(saved));
      await loadOverview();
      setNotice(
        `Draft v${saved.version} saved. ${
          saved.published_version ? 'Learners still see the published version.' : 'Publish it to make it live.'
        }`
      );
      return saved;
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Could not save the draft'));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const handlePublish = async () => {
    if (!draft) return;
    const saved = await persistDraft();
    if (!saved) return;
    try {
      setBusy('publish');
      setError(null);
      const published = await trainingService.publishAdminModule(saved.slug);
      setDraft(toDraft(published));
      await loadOverview();
      setNotice(`v${published.version} is now live for learners.`);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Publishing failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleArchive = async () => {
    if (!draft?.slug) return;
    if (!window.confirm('Archive this module? Learners will no longer see it, but history is kept.')) {
      return;
    }
    try {
      setBusy('archive');
      await trainingService.archiveAdminModule(draft.slug);
      setEditorOpen(false);
      setDraft(null);
      setPreviewOpen(false);
      await loadOverview();
      setNotice('Module archived. Its versions and completion history are retained.');
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Archive failed'));
    } finally {
      setBusy(null);
    }
  };

  const updateLesson = (index: number, patch: Partial<LessonDraft>) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            lessons: prev.lessons.map((lesson, lessonIndex) =>
              lessonIndex === index ? { ...lesson, ...patch } : lesson
            ),
          }
        : prev
    );
  };

  const updateQuestion = (index: number, patch: Partial<QuestionDraft>) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            quiz: prev.quiz.map((question, questionIndex) =>
              questionIndex === index ? { ...question, ...patch } : question
            ),
          }
        : prev
    );
  };

  const attachExistingMedia = (lessonIndex: number, asset: AcademyMediaAsset) => {
    const mediaItem: LessonMediaItem = {
      asset_id: asset.id,
      type: mediaTypeFor(asset.content_type),
      caption: asset.caption || '',
      alt_text: asset.alt_text || '',
      url: asset.url,
      filename: asset.filename,
      content_type: asset.content_type,
    };
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            lessons: prev.lessons.map((lesson, index) => {
              if (index !== lessonIndex) return lesson;
              if (lesson.media.some((item) => item.asset_id === asset.id)) return lesson;
              return { ...lesson, media: [...lesson.media, mediaItem] };
            }),
          }
        : prev
    );
    setNotice(`${asset.filename} attached. Save the draft to keep it on this lesson.`);
  };

  const updateMediaItem = (
    lessonIndex: number,
    assetId: number,
    patch: Partial<Pick<LessonMediaItem, 'caption' | 'alt_text'>>
  ) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            lessons: prev.lessons.map((lesson, index) =>
              index === lessonIndex
                ? {
                    ...lesson,
                    media: lesson.media.map((item) =>
                      item.asset_id === assetId ? { ...item, ...patch } : item
                    ),
                  }
                : lesson
            ),
          }
        : prev
    );
  };

  const handleUpload = async (lessonIndex: number, file: File) => {
    try {
      setBusy(`upload-${lessonIndex}`);
      setError(null);
      const asset = await trainingService.uploadMedia(file);
      const mediaItem: LessonMediaItem = {
        asset_id: asset.id,
        type: mediaTypeFor(asset.content_type),
        caption: asset.caption,
        alt_text: asset.alt_text,
        url: asset.url,
        filename: asset.filename,
        content_type: asset.content_type,
      };
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              lessons: prev.lessons.map((lesson, index) =>
                index === lessonIndex ? { ...lesson, media: [...lesson.media, mediaItem] } : lesson
              ),
            }
          : prev
      );
      setOverview((prev) =>
        prev ? { ...prev, media: [asset, ...prev.media.filter((item) => item.id !== asset.id)] } : prev
      );
      setNotice(`${asset.filename} uploaded. Save the draft to attach it to the lesson.`);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Upload failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleDeleteMedia = async (assetId: number) => {
    if (!window.confirm('Retire this file from the media library?')) return;
    try {
      setBusy(`media-${assetId}`);
      await trainingService.deleteMedia(assetId);
      await loadOverview();
      setNotice('Media file retired.');
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Could not retire that file'));
    } finally {
      setBusy(null);
    }
  };

  if (editorOpen && draft) {
    const hasDraftVersion = draft.draftVersion !== null || draft.status === 'draft';
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => {
                setEditorOpen(false);
                setDraft(null);
                setPreviewOpen(false);
                setError(null);
                setNotice(null);
              }}
              className="mb-2 text-xs font-semibold text-slate-500 hover:text-slate-800"
            >
              ← Back to modules
            </button>
            <h2 className="truncate text-lg font-bold text-slate-900">
              {draft.title || 'New module'}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {draft.slug ? (
                <>
                  <span className={`mr-2 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusStyle(draft.status)}`}>
                    {draft.status}
                  </span>
                  {draft.publishedVersion ? `live v${draft.publishedVersion}` : 'not live'}
                  {draft.draftVersion ? ` · draft v${draft.draftVersion}` : ''}
                </>
              ) : (
                'Saving creates a draft. Nothing reaches learners until you publish.'
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="rounded-lg border border-purple-300 bg-purple-50 px-4 py-2 text-xs font-semibold text-purple-800 hover:bg-purple-100"
            >
              Preview
            </button>
            <button
              type="button"
              onClick={persistDraft}
              disabled={busy !== null}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {busy === 'save' ? 'Saving...' : 'Save draft'}
            </button>
            <button
              type="button"
              onClick={handlePublish}
              disabled={busy !== null}
              className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {busy === 'publish' ? 'Publishing...' : hasDraftVersion ? 'Publish draft' : 'Publish'}
            </button>
            {draft.slug && (
              <>
                <a
                  href="/academy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                >
                  View learner page
                </a>
                <button
                  type="button"
                  onClick={handleArchive}
                  disabled={busy !== null}
                  className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                >
                  {busy === 'archive' ? 'Archiving...' : 'Archive'}
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}
        {notice && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {notice}
          </div>
        )}

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900">Module details</h3>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-xs font-semibold text-slate-600">
              Title
              <input
                value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Category
              <input
                value={draft.category}
                onChange={(event) => setDraft({ ...draft, category: event.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Display duration
              <input
                value={draft.duration}
                onChange={(event) => setDraft({ ...draft, duration: event.target.value })}
                placeholder="2 hours"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Duration (minutes)
              <input
                type="number"
                min={0}
                value={draft.durationMinutes}
                onChange={(event) =>
                  setDraft({ ...draft, durationMinutes: Number(event.target.value) })
                }
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              ISO clause
              <input
                value={draft.isoClause}
                onChange={(event) => setDraft({ ...draft, isoClause: event.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Catalog order
              <input
                type="number"
                value={draft.sortOrder}
                onChange={(event) => setDraft({ ...draft, sortOrder: Number(event.target.value) })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600 md:col-span-2">
              Description
              <textarea
                rows={3}
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
              />
            </label>
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Lessons</h3>
              <p className="text-xs text-slate-500">
                Drag-free ordering: use the arrows. Media is stored in object storage, never the database.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDraft({ ...draft, lessons: [...draft.lessons, blankLesson()] })}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              + Add lesson
            </button>
          </div>

          {draft.lessons.map((lesson, index) => (
            <div key={`lesson-${index}-${lesson.id || 'new'}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Lesson {index + 1}
                </h4>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      setDraft({ ...draft, lessons: moveItem(draft.lessons, index, index - 1) })
                    }
                    disabled={index === 0}
                    className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft({ ...draft, lessons: moveItem(draft.lessons, index, index + 1) })
                    }
                    disabled={index === draft.lessons.length - 1}
                    className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        lessons: draft.lessons.filter((_, lessonIndex) => lessonIndex !== index),
                      })
                    }
                    className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="text-xs font-semibold text-slate-600">
                  Title
                  <input
                    value={lesson.title}
                    onChange={(event) => updateLesson(index, { title: event.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
                  />
                </label>
                <label className="text-xs font-semibold text-slate-600">
                  Duration
                  <input
                    value={lesson.duration}
                    onChange={(event) => updateLesson(index, { duration: event.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
                  />
                </label>
                <label className="text-xs font-semibold text-slate-600 md:col-span-2">
                  Summary
                  <textarea
                    rows={2}
                    value={lesson.summary}
                    onChange={(event) => updateLesson(index, { summary: event.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
                  />
                </label>
                <label className="text-xs font-semibold text-slate-600 md:col-span-2">
                  Lesson body (Markdown)
                  <textarea
                    rows={10}
                    value={lesson.contentMarkdown}
                    onChange={(event) => updateLesson(index, { contentMarkdown: event.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs text-slate-900 focus:border-slate-500 focus:outline-none"
                  />
                </label>
                <label className="text-xs font-semibold text-slate-600 md:col-span-2">
                  Key takeaways (one per line)
                  <textarea
                    rows={3}
                    value={lesson.takeawaysText}
                    onChange={(event) => updateLesson(index, { takeawaysText: event.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
                  />
                </label>
              </div>

              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-xs font-semibold text-slate-700">Lesson media</div>
                    <div className="text-[11px] text-slate-500">
                      Images, MP4/WebM video, or a PDF handout. Up to 50 MB.
                    </div>
                  </div>
                  <label className="cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100">
                    {busy === `upload-${index}` ? 'Uploading...' : 'Upload media'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,application/pdf"
                      className="hidden"
                      disabled={busy !== null}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (file) handleUpload(index, file);
                      }}
                    />
                  </label>
                </div>

                {lesson.media.length === 0 ? (
                  <p className="mt-3 text-xs text-slate-400">No media attached to this lesson.</p>
                ) : (
                  <ul className="mt-3 space-y-3">
                    {lesson.media.map((item) => (
                      <li
                        key={item.asset_id}
                        className="rounded-lg border border-slate-200 bg-white p-3"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-3">
                            {item.type === 'image' ? (
                              <img
                                src={trainingService.mediaUrl(item.url)}
                                alt={item.alt_text || item.filename || 'Lesson media'}
                                className="h-10 w-14 rounded object-cover"
                              />
                            ) : (
                              <span className="flex h-10 w-14 items-center justify-center rounded bg-slate-100 text-[10px] font-bold uppercase text-slate-500">
                                {item.type}
                              </span>
                            )}
                            <div className="min-w-0">
                              <div className="truncate text-xs font-medium text-slate-800">
                                {item.filename || `Asset ${item.asset_id}`}
                              </div>
                              <div className="text-[11px] capitalize text-slate-500">
                                {item.type} attachment
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              updateLesson(index, {
                                media: lesson.media.filter(
                                  (media) => media.asset_id !== item.asset_id
                                ),
                              })
                            }
                            className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                          >
                            Detach
                          </button>
                        </div>

                        <div
                          className={`mt-3 grid grid-cols-1 gap-2 ${
                            item.type === 'image' ? 'sm:grid-cols-2' : ''
                          }`}
                        >
                          <label className="text-[11px] font-semibold text-slate-600">
                            Caption
                            <input
                              value={item.caption || ''}
                              maxLength={512}
                              onChange={(event) =>
                                updateMediaItem(index, item.asset_id, {
                                  caption: event.target.value,
                                })
                              }
                              placeholder="Shown beneath the media"
                              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
                            />
                          </label>
                          {item.type === 'image' && (
                            <label className="text-[11px] font-semibold text-slate-600">
                              Alt text
                              <input
                                value={item.alt_text || ''}
                                maxLength={255}
                                onChange={(event) =>
                                  updateMediaItem(index, item.asset_id, {
                                    alt_text: event.target.value,
                                  })
                                }
                                placeholder="Describe the image for screen readers"
                                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
                              />
                            </label>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                <LessonMediaPicker
                  assets={overview?.media || []}
                  attachedIds={lesson.media.map((item) => item.asset_id)}
                  onAttach={(asset) => attachExistingMedia(index, asset)}
                  disabled={busy !== null}
                />
              </div>
            </div>
          ))}
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Knowledge check</h3>
              <p className="text-xs text-slate-500">
                Learners need 70% to pass. The correct answer stays server-side.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDraft({ ...draft, quiz: [...draft.quiz, blankQuestion()] })}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              + Add question
            </button>
          </div>

          {draft.quiz.map((question, index) => {
            const optionCount = question.optionsText
              .split('\n')
              .map((option) => option.trim())
              .filter(Boolean).length;
            return (
              <div key={`question-${index}-${question.id || 'new'}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Question {index + 1}
                  </h4>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        quiz: draft.quiz.filter((_, questionIndex) => questionIndex !== index),
                      })
                    }
                    className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                  >
                    Remove
                  </button>
                </div>
                <div className="mt-3 space-y-3">
                  <label className="block text-xs font-semibold text-slate-600">
                    Question
                    <textarea
                      rows={2}
                      value={question.question}
                      onChange={(event) => updateQuestion(index, { question: event.target.value })}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
                    />
                  </label>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="text-xs font-semibold text-slate-600">
                      Options (one per line)
                      <textarea
                        rows={4}
                        value={question.optionsText}
                        onChange={(event) => {
                          const nextOptions = event.target.value
                            .split('\n')
                            .map((option) => option.trim())
                            .filter(Boolean).length;
                          updateQuestion(index, {
                            optionsText: event.target.value,
                            correctIndex: Math.min(
                              question.correctIndex,
                              Math.max(nextOptions - 1, 0)
                            ),
                          });
                        }}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
                      />
                    </label>
                    <div className="space-y-3">
                      <label className="block text-xs font-semibold text-slate-600">
                        Correct answer
                        <select
                          value={Math.min(question.correctIndex, Math.max(optionCount - 1, 0))}
                          onChange={(event) =>
                            updateQuestion(index, { correctIndex: Number(event.target.value) })
                          }
                          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
                        >
                          {Array.from({ length: Math.max(optionCount, 1) }).map((_, optionIndex) => (
                            <option key={optionIndex} value={optionIndex}>
                              Option {String.fromCharCode(65 + optionIndex)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="block text-xs font-semibold text-slate-600">
                        Explanation
                        <textarea
                          rows={3}
                          value={question.explanation}
                          onChange={(event) => updateQuestion(index, { explanation: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal text-slate-900 focus:border-slate-500 focus:outline-none"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {previewOpen && (
          <AcademyModulePreview
            module={draft}
            status={draft.status}
            onClose={() => setPreviewOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Course content</h2>
          <p className="text-sm text-slate-500">
            Draft and publish modules and lesson media. Published versions stay live while you edit
            the next one.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleImport}
            disabled={busy !== null}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {busy === 'import' ? 'Importing...' : 'Import bundled curriculum'}
          </button>
          <button
            type="button"
            onClick={() => openEditor()}
            className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800"
          >
            + New module
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      {loading && !overview ? (
        <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-slate-200 bg-white">
          <p className="text-sm text-slate-500">Loading course content...</p>
        </div>
      ) : overview ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatChip label="Published modules" value={overview.published_module_count} />
            <StatChip label="Open drafts" value={overview.draft_module_count} />
            <StatChip label="Media files" value={overview.media.length} />
          </div>

          {!overview.initialized && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Learners are currently seeing the curriculum bundled in the application. Import it to
              start editing and publishing from here.
            </div>
          )}

          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Module</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold">Lessons</th>
                    <th className="px-5 py-3 font-semibold">Questions</th>
                    <th className="px-5 py-3 font-semibold">Updated</th>
                    <th className="px-5 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {overview.modules.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-5 py-8 text-sm text-slate-500">
                        No modules yet. Import the bundled curriculum or create one.
                      </td>
                    </tr>
                  ) : (
                    overview.modules.map((module: AdminAcademyModuleSummary) => (
                      <tr key={module.slug} className="hover:bg-slate-50">
                        <td className="px-5 py-3">
                          <div className="font-medium text-slate-900">{module.title}</div>
                          <div className="text-xs text-slate-500">{module.category || module.slug}</div>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusStyle(module.status)}`}>
                            {module.status}
                          </span>
                          <div className="mt-1 text-[11px] text-slate-500">
                            {module.published_version ? `live v${module.published_version}` : 'not live'}
                            {module.draft_version ? ` · draft v${module.draft_version}` : ''}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-slate-700">{module.lesson_count}</td>
                        <td className="px-5 py-3 text-slate-700">{module.question_count}</td>
                        <td className="px-5 py-3 text-xs text-slate-500">
                          {module.updated_at
                            ? new Date(module.updated_at).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : '—'}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEditor(module.slug)}
                              className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                            >
                              Edit
                            </button>
                            {module.published_version && (
                              <a
                                href="/academy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                              >
                                View
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4">
              <h3 className="text-sm font-bold text-slate-900">Media library</h3>
              <p className="text-xs text-slate-500">
                Files uploaded from the lesson editor. Reuse them with Attach from library in any
                lesson; attached files cannot be retired until they are detached.
              </p>
            </div>
            {overview.media.length === 0 ? (
              <p className="px-5 py-8 text-sm text-slate-500">No media uploaded yet.</p>
            ) : (
              <ul className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2 xl:grid-cols-3">
                {overview.media.map((asset) => (
                  <li
                    key={asset.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      {asset.content_type.startsWith('image/') ? (
                        <img
                          src={trainingService.mediaUrl(asset.url)}
                          alt={asset.alt_text || asset.filename}
                          className="h-12 w-16 rounded object-cover"
                        />
                      ) : (
                        <span className="flex h-12 w-16 items-center justify-center rounded bg-slate-100 text-[10px] font-bold uppercase text-slate-500">
                          {asset.content_type.split('/')[0]}
                        </span>
                      )}
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium text-slate-800">{asset.filename}</div>
                        <div className="text-[11px] text-slate-500">{formatBytes(asset.size_bytes)}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteMedia(asset.id)}
                      disabled={busy !== null}
                      className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-40"
                    >
                      Retire
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
};

export default AcademyContentManager;
